import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { useFormStore } from '../store/formStore'
import { delay } from '../mock/mockDelay'
import { requestOtp, verifyOtp } from '../api/applicant'
import { ApiError } from '../api/client'
import { useSession } from '../apply/session'
import { resumeRoute } from '../apply/sections'
import { OTPInput } from '../components/fields/OTPInput'
import { LogoHeader } from '../components/layout'
import { Button } from '../components/ui/Button'

type VerifyState = 'idle' | 'verifying' | 'success' | 'error' | 'locked'

export function VerifyScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const formData = useFormStore((s) => s.formData)
  const triggerWelcomeWizard = useFormStore((s) => s.triggerWelcomeWizard)
  const { applySession } = useSession()
  const authEmail = (formData['authEmail'] as string | undefined) ?? ''
  const shouldReduceMotion = useReducedMotion()

  // The login screen passes the server's Retry-After when a still-valid code
  // was already sent; otherwise mirror the backend's 60s resend cooldown.
  const initialCooldown =
    (location.state as { cooldownSeconds?: number } | null)?.cooldownSeconds ?? 60

  const [verifyState, setVerifyState] = useState<VerifyState>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [countdown, setCountdown] = useState(initialCooldown)
  const [canResend, setCanResend] = useState(initialCooldown <= 0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startCooldown = useCallback((seconds: number) => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (seconds <= 0) {
      setCanResend(true)
      setCountdown(0)
      return
    }
    setCanResend(false)
    setCountdown(seconds)
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!)
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  // Start countdown on mount
  useEffect(() => {
    startCooldown(initialCooldown)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [otpShake, setOtpShake] = useState(false)
  const [otpKey, setOtpKey] = useState(0) // incrementing key forces OTPInput remount to clear inputs

  const showError = async (message: string) => {
    setErrorMessage(message)
    setVerifyState('error')
    setOtpShake(true)
    // After shake animation completes, clear and go back to idle
    await delay(600)
    setOtpShake(false)
    await delay(200)
    setOtpKey((k) => k + 1) // remount OTPInput to clear inputs
    setVerifyState('idle')
  }

  const handleOTPComplete = async (code: string) => {
    if (verifyState !== 'idle') return

    setVerifyState('verifying')

    try {
      const session = await verifyOtp(authEmail, code)
      setVerifyState('success')
      triggerWelcomeWizard()
      await delay(800)
      // Installing the session flips the route guard, which redirects to the
      // resume target; navigate explicitly as well for clarity.
      applySession(session)
      navigate(resumeRoute(session.application))
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403 || err.code === 'account_locked') {
          setErrorMessage(
            'Your account access is locked. Please contact support so they can verify your identity and restore access.',
          )
          setVerifyState('locked')
          setCanResend(false)
          if (timerRef.current) clearInterval(timerRef.current)
          return
        }
        if (err.code === 'code_expired') {
          setCanResend(true)
          if (timerRef.current) clearInterval(timerRef.current)
          await showError('That code has expired. Request a new one below.')
          return
        }
        if (err.code === 'code_invalid') {
          const remaining = Number(err.details['remaining_attempts'])
          await showError(
            Number.isFinite(remaining) && remaining > 0
              ? `That code is incorrect. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
              : 'That code is incorrect. Please try again or request a new code.',
          )
          return
        }
      }
      await showError('Something went wrong while verifying. Please try again.')
    }
  }

  const handleResend = async () => {
    if (!canResend) return
    try {
      await requestOtp(authEmail)
      startCooldown(60)
      if (verifyState === 'locked') setVerifyState('idle')
      setOtpKey((k) => k + 1)
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        startCooldown(err.retryAfterSeconds || 60)
        return
      }
      setErrorMessage('Could not resend the code. Please try again.')
      setVerifyState('error')
      await delay(1500)
      setVerifyState((s) => (s === 'error' ? 'idle' : s))
    }
  }

  const handleChangeEmail = () => {
    navigate('/login')
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.4,
        ease: [0.0, 0.0, 0.2, 1.0] as [number, number, number, number],
      },
    },
  }

  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`
  }

  const isDisabled = verifyState !== 'idle' && verifyState !== 'error'

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-surface-base)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-4)',
      }}
    >
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        style={{
          background: 'var(--color-surface-container-lowest)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-ambient)',
          padding: 'var(--spacing-10)',
          width: '100%',
          maxWidth: '440px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Zota Logo */}
        <LogoHeader animateEnvelope />

        {/* Success overlay */}
        <AnimatePresence>
          {verifyState === 'success' && (
            <motion.div
              key="success-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
              style={{
                position: 'absolute',
                inset: 0,
                background: 'var(--color-surface-container-lowest)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--spacing-4)',
                zIndex: 10,
              }}
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={
                  shouldReduceMotion
                    ? { duration: 0 }
                    : { type: 'spring', stiffness: 300, damping: 24 }
                }
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'var(--color-success-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M7 16l6 6 12-12"
                    stroke="var(--color-success-text)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>
              <p
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 'var(--weight-semibold)',
                  fontSize: 'var(--text-title-sm)',
                  color: 'var(--color-success-text)',
                  margin: 0,
                }}
              >
                Verified
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Verifying spinner overlay */}
        <AnimatePresence>
          {verifyState === 'verifying' && (
            <motion.div
              key="verifying-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.15 }}
              style={{
                position: 'absolute',
                inset: 0,
                background: 'var(--glass-overlay-bg)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--spacing-4)',
                zIndex: 10,
                backdropFilter: 'var(--glass-subtle-blur)',
              }}
            >
              <LargeSpinner />
              <p
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-body-sm)',
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                }}
              >
                Verifying...
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error inline message */}
        <AnimatePresence>
          {(verifyState === 'error' || verifyState === 'locked') && (
            <motion.div
              key="error-message"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
              style={{
                padding: '10px 16px',
                background: 'var(--color-red-50)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--spacing-4)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-sm)',
                color: 'var(--color-error-text)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              {errorMessage || 'That code is incorrect. Please try again or request a new code.'}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Heading */}
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--weight-semibold)',
            fontSize: 'var(--text-title-lg)',
            color: 'var(--color-text-primary)',
            lineHeight: 'var(--leading-tight)',
            margin: '0 0 var(--spacing-3) 0',
          }}
        >
          Check your email
        </h1>

        {/* Subtitle */}
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-md)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--leading-normal)',
            margin: '0 0 var(--spacing-2) 0',
          }}
        >
          We sent a 6-digit verification code to{' '}
          <strong style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--weight-semibold)' }}>
            {authEmail || 'your email'}
          </strong>
          . Enter it below or click the link in your email.
        </p>

        {/* Helper text */}
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-text-tertiary)',
            margin: '0 0 var(--spacing-8) 0',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          The code expires in 15 minutes. Check your spam folder if you do not see the email.
        </p>

        {/* OTP Input */}
        <motion.div
          animate={
            otpShake && !shouldReduceMotion
              ? { x: [0, -8, 8, -8, 8, 0] }
              : { x: 0 }
          }
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          style={{
            marginBottom: 'var(--spacing-6)',
            borderRadius: 'var(--radius-md)',
            border: verifyState === 'error' ? '1.5px solid var(--color-error-text)' : 'none',
            padding: verifyState === 'error' ? 'var(--spacing-1)' : '0',
          }}
          aria-label="Verification code"
        >
          <OTPInput key={otpKey} onComplete={handleOTPComplete} disabled={isDisabled} />
        </motion.div>

        {/* Resend link */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {canResend ? (
            <Button variant="link" onClick={handleResend}>
              Resend code
            </Button>
          ) : (
            <span
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-sm)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              Resend code (available in {formatCountdown(countdown)})
            </span>
          )}
        </div>

        {/* Change email link */}
        <div style={{ textAlign: 'center' }}>
          <Button variant="link" onClick={handleChangeEmail}>
            Use a different email address
          </Button>
        </div>
      </motion.div>
    </div>
  )
}

function LargeSpinner() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      style={{ animation: 'spin 0.7s linear infinite' }}
      aria-hidden="true"
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <circle
        cx="20"
        cy="20"
        r="16"
        stroke="var(--color-outline-subtle)"
        strokeWidth="3"
      />
      <circle
        cx="20"
        cy="20"
        r="16"
        stroke="var(--color-action-primary)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="70"
        strokeDashoffset="50"
      />
    </svg>
  )
}
